<template>
  <div class="page">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
      <h2 class="page-title" style="margin:0;">{{ $t('menu.handlerInProgress') }}</h2>
      <el-button @click="load" :loading="loading">{{ $t('common.refresh') }}</el-button>
    </div>

    <div class="work-grid">
      <section class="glass-card work-panel">
        <div class="work-panel__head">
          <div class="work-panel__title">{{ $t('secondary.assignedCases') }}</div>
          <span class="tag-pill">{{ assignedCases.length }}</span>
        </div>
        <el-table :data="assignedPage" style="width:100%;" @row-click="openCase">
          <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
          <el-table-column prop="title" :label="$t('field.title')" min-width="180" />
          <el-table-column :label="$t('field.status')" width="160">
            <template slot-scope="s">
              <span class="status-pill-wrap">
                <span :class="['tag-pill', s.row.hasReminder ? 'tag-pill--with-dot' : '']">{{ caseStatusLabel(s.row) }}</span>
                <span v-if="s.row.hasReminder" class="status-pill-dot" />
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="180" />
        </el-table>
        <Pager :total="assignedCases.length" :page.sync="assignedPageNo" :page-size.sync="pageSize" />
      </section>

      <section class="glass-card work-panel">
        <div class="work-panel__head">
          <div class="work-panel__title">{{ $t('secondary.escalatedReturnedCases') }}</div>
          <span class="tag-pill tag-pill--warn">{{ returnedCases.length }}</span>
        </div>
        <el-table :data="returnedPage" style="width:100%;" @row-click="openCase">
          <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
          <el-table-column prop="title" :label="$t('field.title')" min-width="180" />
          <el-table-column :label="$t('field.status')" width="160">
            <template slot-scope="s">
              <span class="status-pill-wrap">
                <span :class="['tag-pill', s.row.hasReminder ? 'tag-pill--with-dot' : '']">{{ caseStatusLabel(s.row) }}</span>
                <span v-if="s.row.hasReminder" class="status-pill-dot" />
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="180" />
        </el-table>
        <Pager :total="returnedCases.length" :page.sync="returnedPageNo" :page-size.sync="pageSize" />
      </section>

      <section class="glass-card work-panel work-panel--wide">
        <div class="work-panel__head">
          <div class="work-panel__title">{{ $t('secondary.disputeCases') }}</div>
          <span class="tag-pill tag-pill--warn">{{ activeDisputes.length }}</span>
        </div>
        <el-table :data="disputePage" style="width:100%;" @row-click="openCase">
          <el-table-column prop="caseNo" :label="$t('field.caseNo')" width="140" />
          <el-table-column prop="caseTitle" :label="$t('field.title')" min-width="220" />
          <el-table-column prop="status" :label="$t('field.status')" width="180">
            <template slot-scope="s">
              <span class="status-pill-wrap">
                <span :class="['tag-pill', s.row.hasReminder ? 'tag-pill--with-dot' : '']">{{ $t(`status.dispute.${s.row.status}`) }}</span>
                <span v-if="s.row.hasReminder" class="status-pill-dot" />
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="createdAtLabel" :label="$t('field.createdAt')" width="180" />
        </el-table>
        <Pager :total="activeDisputes.length" :page.sync="disputePageNo" :page-size.sync="pageSize" />
      </section>
    </div>

    <SecondaryCaseDialog
      v-model="detailDialog"
      :case-no="currentCaseNo"
      mode="work"
      @reminder-read="onReminderRead"
      @changed="load"
    />
  </div>
</template>

<script>
import { secondaryPendingCases, secondaryDisputes } from '../../api/secondary';
import SecondaryCaseDialog from '../../components/SecondaryCaseDialog.vue';
import { caseStatusLabel as formatCaseStatusLabel } from '../../utils/caseDisplay';

const Pager = {
  props: {
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 }
  },
  render(h) {
    return h('div', { style: { display: 'flex', justifyContent: 'flex-end', marginTop: '12px' } }, [
      h('el-pagination', {
        props: {
          background: true,
          layout: 'total, prev, pager, next',
          total: this.total,
          pageSize: this.pageSize,
          currentPage: this.page
        },
        on: {
          'current-change': (p) => this.$emit('update:page', p)
        }
      })
    ]);
  }
};

export default {
  name: 'SecondaryPending',
  components: { SecondaryCaseDialog, Pager },
  data() {
    return {
      loading: false,
      cases: [],
      disputes: [],
      assignedPageNo: 1,
      returnedPageNo: 1,
      disputePageNo: 1,
      pageSize: 10,
      detailDialog: false,
      currentCaseNo: ''
    };
  },
  computed: {
    version() {
      return this.$store.state.versions.secondaryCases;
    },
    disputeVersion() {
      return this.$store.state.versions.disputes;
    },
    assignedCases() {
      return (this.cases || []).filter((x) => x.status === 'PENDING_SECONDARY');
    },
    returnedCases() {
      return (this.cases || []).filter((x) => x.status === 'ESCALATED_RETURNED');
    },
    activeDisputes() {
      return (this.disputes || []).filter((x) => x.status === 'DISPUTE_SECONDARY_CREATED' || x.status === 'DISPUTE_SECONDARY_IN_PROGRESS');
    },
    assignedPage() {
      return this.slice(this.assignedCases, this.assignedPageNo);
    },
    returnedPage() {
      return this.slice(this.returnedCases, this.returnedPageNo);
    },
    disputePage() {
      return this.slice(this.activeDisputes, this.disputePageNo);
    }
  },
  watch: {
    version() {
      this.load();
    },
    disputeVersion() {
      this.load();
    },
    detailDialog(v) {
      if (v) return;
      if (this.$route.query && this.$route.query.caseNo) {
        const q = { ...this.$route.query };
        delete q.caseNo;
        this.$router.replace({ path: this.$route.path, query: q }).catch(() => {});
      }
    },
    '$route.query.caseNo'() {
      this.maybeOpenFromQuery();
    }
  },
  created() {
    this.load();
    this.maybeOpenFromQuery();
  },
  methods: {
    maybeOpenFromQuery() {
      const caseNo = this.$route.query && this.$route.query.caseNo ? String(this.$route.query.caseNo) : '';
      if (!caseNo) return;
      this.currentCaseNo = caseNo;
      this.detailDialog = true;
    },
    slice(list, page) {
      const start = (page - 1) * this.pageSize;
      return (list || []).slice(start, start + this.pageSize);
    },
    labelTime(ts) {
      return ts ? new Date(ts).toLocaleString() : '-';
    },
    caseStatusLabel(row) {
      return formatCaseStatusLabel(this, row);
    },
    async load() {
      this.loading = true;
      try {
        const [cases, disputes] = await Promise.all([secondaryPendingCases(), secondaryDisputes()]);
        this.cases = (cases || []).map((x) => ({ ...x, createdAtLabel: this.labelTime(x.createdAt) }));
        this.disputes = (disputes || []).map((x) => ({ ...x, createdAtLabel: this.labelTime(x.createdAt) }));
        this.$store.dispatch('refreshSecondaryBadges');
      } finally {
        this.loading = false;
      }
    },
    openCase(row) {
      if (!row || !row.caseNo) return;
      this.currentCaseNo = row.caseNo;
      this.detailDialog = true;
    },
    onReminderRead(caseNo) {
      if (!caseNo) return;
      this.cases = (this.cases || []).map((x) => String(x.caseNo) === String(caseNo) ? { ...x, hasReminder: false } : x);
      this.disputes = (this.disputes || []).map((x) => String(x.caseNo) === String(caseNo) ? { ...x, hasReminder: false } : x);
      this.$store.dispatch('refreshSecondaryBadges');
    }
  }
};
</script>

<style scoped>
.work-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}
.work-panel {
  padding: 14px;
  overflow-x: auto;
}
.work-panel--wide {
  grid-column: 1 / -1;
}
.work-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.work-panel__title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
}
.status-pill-wrap {
  position: relative;
  display: inline-block;
}
.tag-pill--with-dot {
  padding-right: 18px;
}
.status-pill-dot {
  position: absolute;
  top: 3px;
  right: 5px;
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.95);
}
@media (max-width: 980px) {
  .work-grid {
    grid-template-columns: 1fr;
  }
}
</style>
